'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import React from 'react';
import { CheckCircle2, Circle, XCircle, Terminal, Eye, RefreshCw, Loader2 } from 'lucide-react';

// FM check text is MARKDOWN: **bold** and `code`. Rendering it raw prints literal
// asterisks and backticks on screen — the factory has a whole gate against exactly
// that leak (rsc-markdown-leak-gate). Render it, don't print it.
function md(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|`([^`]+)`/g;
  let last = 0, m: RegExpExecArray | null, k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      out.push(<strong key={k++} className="font-semibold text-gray-200">{m[1]}</strong>);
    } else {
      out.push(
        <code key={k++} className="px-1 py-px rounded bg-white/5 text-[10px] text-cyan-300/90">
          {m[2]}
        </code>,
      );
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// THE DOCUMENT ON ONE SIDE, THE EVIDENCE ON THE OTHER.
//
// Boss: "I see it on one side the document, another side is the code. And I see as
// far as I go in the code, the checklist gets crossed over. That way we will never
// miss a single point."
//
// Position first (his priority C): the cursor row is marked YOU ARE HERE. Evidence
// second: every green row can be opened and its proof read. Nothing here writes —
// the ledger has exactly one writer (Beast, via beast-run.sh).

type Row = {
  id: string; line: number; stage: number | null;
  cls: 'command' | 'judgement'; section: string; text: string;
  proved_by: string | null;
};
type LedgerRow = {
  state: string; class: string; evidence: string;
  at_commit: string | null; ts: string | null; proved_by: string | null;
};

// 3.5 = the AUDIT block — ~300 FACTORY-MASTER checks with no stage of their
// own (PART B, global rules, page-type rules). The run walks them between
// build and ship, so the picker shows them there. Boss: "Audit is a massive
// thing" — it was invisible here because this list only knew stages 0-4.
const STAGE_NAMES: Record<number, string> = {
  0: 'Research', 1: 'URL Strategy', 2: 'Design', 3: 'Content + Build',
  3.5: 'Audit', 4: 'Ship',
};
const STAGE_TABS = [0, 1, 2, 3, 3.5, 4];
const stageLabel = (s: number) => `${Number.isInteger(s) ? s : 'A'} · ${STAGE_NAMES[s]}`;

export function PipelineDoc({ slug, path }: { slug: string; path: string }) {
  // Open at the LIVE stage, not always Research. Boss: 'if I go to build, it
  // shouldn't start from the first tab.' A manual tab choice is remembered per
  // project; with no saved choice the view follows the running step.
  const [stage, setStage] = useState<number>(() => {
    try {
      const s = localStorage.getItem(`beast:pipeline:stage:${slug}`);
      if (s !== null) return JSON.parse(s);
    } catch {}
    return -1;                       // -1 = not chosen yet — follow the live run
  });
  const [live, setLive] = useState<{ first_check?: string; checks?: string[];
                                     stage?: number; label?: string } | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [checks, setChecks] = useState<Record<string, LedgerRow>>({});
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [runOut, setRunOut] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [pid, setPid] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    // where is the machine RIGHT NOW?
    let lv: any = null;
    try {
      const s = await fetch(`/api/pipeline/loop?path=${encodeURIComponent(path)}`)
        .then(r => r.json());
      lv = s?.live ?? null;
      setLive(lv);
    } catch {}
    const effStage = stage >= 0 ? stage : (lv?.stage ?? 0);
    if (stage < 0 && lv?.stage != null) setStage(lv.stage);   // lock onto the live tab once
    const d = await fetch(`/api/pipeline/document?stage=${effStage}`)
      .then(r => r.json()).catch(() => ({ rows: [] }));
    setRows(d.rows || []);

    const c = await fetch('/api/checkpoint', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paths: { [slug]: path } }),
    }).then(r => r.json()).catch(() => null);

    const led = c?.checkpoints?.[slug]?.ledger ?? null;
    setCursor(led?.cursor ?? null);
    setChecks(led?.rows ?? {});
    setCounts(led?.counts ?? null);
    setLoading(false);
  }, [stage, slug, path]);

  useEffect(() => { load(); }, [load]);

  // Scroll to the RUNNING NOW row ONCE per position — not on every poll. The
  // first version put scrollIntoView in the row's ref callback, which fires on
  // every render: each data refresh yanked the list back to the anchor and
  // Boss literally could not scroll away. Scrolls only the list container
  // (never the page), and only when the live check actually changes.
  const listRef = useRef<HTMLDivElement | null>(null);
  const lastAnchor = useRef<string | null>(null);
  useEffect(() => {
    const target = live?.first_check;
    if (!target || loading || lastAnchor.current === `${stage}:${target}`) return;
    const box = listRef.current;
    const el = box?.querySelector<HTMLElement>(`[data-check="${CSS.escape(target)}"]`);
    if (box && el) {
      box.scrollTop = el.offsetTop - box.clientHeight / 2;
      lastAnchor.current = `${stage}:${target}`;
    }
  }, [live?.first_check, stage, loading]);

  // LAUNCH: detached. The button returns in milliseconds and a poller follows the
  // log, so Boss can start a second project immediately instead of watching a
  // spinner for ten minutes.
  // Nothing launches from this view any more (see the header comment). But a
  // stage walk may be running from elsewhere — check once on load so the
  // indicator tells the truth instead of never firing.
  useEffect(() => {
    let dead = false;
    fetch(`/api/pipeline/run?path=${encodeURIComponent(path)}&stage=${stage}`)
      .then(x => x.json())
      .then(s => {
        if (dead || !s?.running) return;
        setRunning(true);
        setPid(s.pid ? String(s.pid) : null);
      })
      .catch(() => {});
    return () => { dead = true; };
  }, [path, stage]);

  // Poll only while something is actually running — an always-on interval would
  // hammer the API for every open tab.
  useEffect(() => {
    if (!running) return;
    const t = setInterval(async () => {
      const s = await fetch(
        `/api/pipeline/run?path=${encodeURIComponent(path)}&stage=${stage}`,
      ).then(x => x.json()).catch(() => null);
      if (!s) return;
      setRunOut(s.out || '');
      if (!s.running) { setRunning(false); setPid(null); load(); }
    }, 2000);
    return () => clearInterval(t);
  }, [running, path, stage, load]);

  const icon = (id: string) => {
    const st = checks[id]?.state;
    if (st === 'pass') return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />;
    if (st === 'fail') return <XCircle className="h-3.5 w-3.5 text-red-400 shrink-0" />;
    return <Circle className="h-3.5 w-3.5 text-gray-700 shrink-0" />;
  };

  const active = sel ? checks[sel] : null;
  const selRow = sel ? rows.find(r => r.id === sel) : null;

  return (
    <div className="flex flex-col gap-3">
      {/* stage selector + counts + re-run */}
      <div className="flex items-center gap-2 flex-wrap">
        {STAGE_TABS.map(s => (
          <button key={s} onClick={() => { setStage(s); setSel(null);
              try { localStorage.setItem(`beast:pipeline:stage:${slug}`, JSON.stringify(s)); } catch {} }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              stage === s
                ? 'bg-cyan-500/20 text-cyan-300 ring-1 ring-cyan-500/40'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
            {stageLabel(s)}
          </button>
        ))}
        <div className="flex-1" />
        {counts && (
          <span className="text-[11px] text-gray-500">
            <span className="text-emerald-400">{counts.pass} pass</span>
            {' · '}<span className="text-red-400">{counts.fail} fail</span>
            {' · '}{counts.total} rows
          </span>
        )}
        {running && (
          <span className="flex items-center gap-1.5 text-[11px] text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            running{pid ? ` · pid ${pid}` : ''}
          </span>
        )}
        {/* THE LAUNCH BUTTON IS GONE FROM HERE, DELIBERATELY.

            It called beast-run.sh — a stage walker with NO AGENT, which can only
            prove the 93 command checks in all of FACTORY-MASTER. Boss pressed it
            expecting the pipeline and got 'pass=1 fail=1 wait=124' in seconds:
            124 checks answered 'needs an agent verdict'. It looked like a launch
            button and proved nothing.

            Two launch buttons where only one does the work is a trap. The Start
            button above (RunScreen) owns launching; this document is the
            read-only view of where a project stands. The re-run path stays
            available on the API for a deliberate single-stage re-check.
            (2026-08-31: Run + Pipeline merged into one tab — same rule, one
            surface.) */}
        <span className="text-[11px] text-gray-600">
          read-only view — launch with <b className="text-gray-400">▷ Start</b> above
        </span>
      </div>

      {/* Single column on phones. A forced two-column split at 390px squeezes the
          document to ~150px — one word per line, unreadable. Caught under webkit. */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-3 lg:h-[560px]">
        {/* LEFT — the document */}
        <div ref={listRef} className="overflow-y-auto rounded-xl bg-black/30 ring-1 ring-white/10 p-3
                        max-h-[60vh] lg:max-h-none">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2 sticky top-0
                          bg-[#0b0d12]/90 backdrop-blur py-1">
            FACTORY-MASTER.md · {Number.isInteger(stage) ? `stage ${stage}` : 'audit block'} · {rows.length} checks
            <span className="normal-case tracking-normal float-right text-gray-600">
              <span className="text-cyan-300">● running now</span>
              {' · '}<span className="text-amber-400/80">unproven — re-checked at the gate</span>
              {' · '}<span className="line-through">proven</span>
            </span>
          </div>
          {loading && (
            <div className="flex items-center gap-2 text-xs text-gray-500 py-4">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> loading document…
            </div>
          )}
          {!loading && rows.map(r => {
            const st = checks[r.id]?.state;
            // RUNNING NOW = the check the live step is actually chewing — what
            // Boss means by 'where is it'. The old single caret sat on the
            // earliest unproven row, seven sections behind the real work, and
            // read as a lie. That row keeps a quieter, honest label.
            const isLive = !!live?.checks?.includes(r.id);
            const isCursor = r.id === cursor && !isLive;
            return (
              <button key={r.id} onClick={() => setSel(r.id)}
                data-check={r.id}
                className={`w-full text-left flex items-start gap-2 px-2 py-1 rounded mb-0.5
                  ${isLive ? 'bg-cyan-500/15 ring-1 ring-cyan-500/40' :
                    isCursor ? 'bg-amber-500/10 ring-1 ring-amber-500/25' : ''}
                  ${sel === r.id ? 'bg-white/10' : 'hover:bg-white/5'}`}>
                {icon(r.id)}
                <span className="font-mono text-[10px] text-gray-500 w-24 shrink-0 truncate"
                      title={r.id}>{r.id}</span>
                <span className={`text-xs leading-snug flex-1 min-w-0 break-words ${
                  st === 'pass' ? 'line-through text-gray-600' : 'text-gray-300'}`}>
                  {md(r.text)}
                </span>
                {isLive && (
                  <span className="text-[9px] font-bold text-cyan-300 shrink-0 mt-0.5 animate-pulse">
                    ● RUNNING NOW
                  </span>
                )}
                {isCursor && (
                  <span className="text-[9px] font-bold text-amber-400/80 shrink-0 mt-0.5"
                        title="earliest check with no proof yet — the stage gate will sweep it">
                    UNPROVEN · GATE WILL SWEEP
                  </span>
                )}
                {r.cls === 'command'
                  ? <Terminal className="h-3 w-3 text-cyan-500/70 shrink-0 mt-0.5" />
                  : <Eye className="h-3 w-3 text-purple-400/60 shrink-0 mt-0.5" />}
              </button>
            );
          })}
        </div>

        {/* RIGHT — the evidence */}
        <div className="overflow-y-auto rounded-xl bg-black/30 ring-1 ring-white/10 p-3
                        max-h-[50vh] lg:max-h-none">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Evidence</div>

          {runOut && (
            <pre className="whitespace-pre-wrap break-words bg-black/50 rounded p-2 mb-3
                            text-[10px] text-amber-200 max-h-40 overflow-y-auto">{runOut}</pre>
          )}

          {!sel && <div className="text-xs text-gray-600">Select a check on the left.</div>}

          {sel && !active && (
            <div className="space-y-2 text-xs">
              <div className="font-mono text-gray-400">{sel}</div>
              {selRow && <div className="text-gray-500">{selRow.section}</div>}
              <div className="text-gray-600">
                No ledger row yet — this check has not been proved.
              </div>
              {selRow?.proved_by && (
                <div>
                  <div className="text-gray-500 mb-1 mt-2">would be proved by</div>
                  <pre className="whitespace-pre-wrap break-all bg-black/40 rounded p-2
                                  text-[10px] text-cyan-300/70">{selRow.proved_by}</pre>
                </div>
              )}
            </div>
          )}

          {active && (
            <div className="space-y-3 text-xs">
              <div className="font-mono text-gray-400">{sel}</div>
              {selRow && <div className="text-gray-500 text-[11px]">{selRow.section}</div>}
              <div>
                <span className="text-gray-500">state </span>
                <span className={active.state === 'pass' ? 'text-emerald-400' : 'text-red-400'}>
                  {active.state}
                </span>
                <span className="text-gray-600"> · {active.class}</span>
              </div>
              {active.proved_by && (
                <div>
                  <div className="text-gray-500 mb-1">proved by</div>
                  <pre className="whitespace-pre-wrap break-all bg-black/40 rounded p-2
                                  text-[10px] text-cyan-300">{active.proved_by}</pre>
                </div>
              )}
              <div>
                <div className="text-gray-500 mb-1">evidence</div>
                <pre className="whitespace-pre-wrap break-words bg-black/40 rounded p-2
                                text-[10px] text-gray-300 max-h-64 overflow-y-auto">
                  {active.evidence || '(none)'}
                </pre>
              </div>
              <div className="text-gray-600">
                commit <span className="font-mono">{active.at_commit || '—'}</span>
                {active.ts && <> · {active.ts}</>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
